type BuildAddToJobActionUrlProps = {
  actionUrl: string;
  externalIds: string[];
};

const encodePair = ([key, value]: [string, string]) =>
  `${key}=${encodeURIComponent(value)}`;

/**
 * @description Appends the selected candidates to the organisation's configured add-to-job
 * URL as c[0], c[1], ... params. The configured URL carries its own query (the action name),
 * so the params are added to it rather than replacing it.
 *
 * The query is assembled by hand rather than through URLSearchParams, which escapes the
 * brackets to c%5B0%5D. Both forms are the same parameter to an ordinary parser, but only the
 * literal one also suits a destination that reads the query string itself, and it is the form
 * the destination documents. Values are still escaped individually.
 * @returns the URL to open, or null when the configured value is not a URL
 */
export const buildAddToJobActionUrl = ({
  actionUrl,
  externalIds
}: BuildAddToJobActionUrlProps): string | null => {
  let url: URL;

  try {
    url = new URL(actionUrl);
  } catch {
    return null;
  }

  const candidateParams = externalIds.map(
    (externalId, index): [string, string] => [`c[${index}]`, externalId]
  );

  const candidateKeys = new Set(candidateParams.map(([key]) => key));
  // Everything the configured URL already carries, minus any slot about to be filled, so a
  // candidate replaces a value of the same name rather than joining it.
  const configuredParams = [...url.searchParams].filter(
    ([key]) => !candidateKeys.has(key)
  );

  url.search = [...configuredParams, ...candidateParams]
    .map(encodePair)
    .join('&');

  return url.toString();
};
